'use strict';

import { timeFormat } from 'd3-time-format'

// Set smoothing thresholds in meters.
export const SMOOTH_DISTANCE = 10;
export const SMOOTH_HEIGHT = 0.9;

const formatPaceTime = timeFormat("%M:%S");
export const formatSecondsAsPace = function(d) {
  d = Math.round(d);
  if (d > 3599) { // Don't try to format anything over an hour.
    return '--:--';
  }
  return formatPaceTime(new Date(2012, 0, 1, 0, 0, d));
};

export function getFirstValidDatetime(trackpoints) {
  for (var i = 0; i < trackpoints.length; i++) {
    if (trackpoints[i].datetime) {
      return trackpoints[i].datetime;
    }
  }
  return null;
}

export function filterElevationOutlierData(trackpoints) {
  for (let i = trackpoints.length - 1; i >= 0; i--) {
    if (typeof trackpoints[i].altitude_meters != 'undefined' && typeof trackpoints[i+1] != 'undefined' && trackpoints[i].altitude_meters == 0 && (trackpoints[i+1].altitude_meters > 50 || trackpoints[i+1].altitude_meters == null)) {
      trackpoints[i].altitude_meters = null;
    }
  }
  return trackpoints;
}

export const calculateMovingTimeFromTrackpoints = function (trackpoints) {

  var previous_time = trackpoints[0].datetime;
  var milliseconds = 0;
  for (var i in trackpoints) {
    if (trackpoints[i].datetime) {
      var time_difference_milliseconds = trackpoints[i].datetime - previous_time;

      // A break for 20 seconds is when we stop counting moving time.
      if (time_difference_milliseconds < 20000) {
        milliseconds += time_difference_milliseconds;
      }

      previous_time = trackpoints[i].datetime;
    }
  }

  return milliseconds / 1000;
}

export const filterTrackpointsWithoutKey = function (trackpoints, required_key) {

  let filtered_trackpoints = [];

  for (let i = 0; i < trackpoints.length; i++) {
    if (typeof(trackpoints[i][required_key]) != 'undefined') {
      filtered_trackpoints.push(trackpoints[i]);
    }
  }

  return filtered_trackpoints;
} 

// NOTE One posibility would be to change the smooth distance based on speed or maybe just time.
export const getSmoothedElevationPointsByDistance = function (trackpoints, smooth_distance) {

  trackpoints = filterTrackpointsWithoutKey(trackpoints, 'altitude_meters');

  if (trackpoints.length == 0) {
    return [];
  }

  // Smooth the data points by reducing down to an average elevation for a minimum of 10
  // meters. We do this by getting all the elevation data in a 10m span and
  // then dividing by the number of points collected in that 10m+ span. Since
  // the final gain is cumulative we can deal with samples being of slightly
  // varying rates. Wildly different sample distances can occur if we have
  // less data in sections to begin with. This means we might have smoothed over some
  // hills, but in these cases the original data would also be missing that information.
  let buffer_elevation = 0.0;
  let buffer_distance = 0.0;
  let buffer_count = 0;
  let previous_distance_meters = parseFloat(trackpoints[0].distance_meters);

  let smoothed_elevation_points = [];

  for (let i = 0; i < trackpoints.length; i++) {

    let current_elevation = trackpoints[i].altitude_meters;
    let distance_meters = trackpoints[i].distance_meters;
    let distance_delta = distance_meters - previous_distance_meters;

    previous_distance_meters = distance_meters;

    // Add new elevation to the buffer. Rather than summing up at the end we
    // just do it now and count how many points are in the buffer.
    buffer_distance = buffer_distance + distance_delta;
    buffer_elevation = buffer_elevation + current_elevation;
    buffer_count += 1;

    // When distance travelled in the buffer is over the smooth_distance, store the
    // average elevation of the buffer and then clear it.
    //
    // NOTE Here I would to add reference to the starting trackpoint and the end trackpoint.
    if (buffer_distance >= smooth_distance) {

      let average_elevation = buffer_elevation / buffer_count;
      smoothed_elevation_points.push({
        distance: trackpoints[i].distance_meters, // Just use the end distance now.
        elevation: average_elevation,
      });

      buffer_elevation = 0.0;
      buffer_distance = 0.0;
      buffer_count = 0;

    }
  }

  return smoothed_elevation_points;
}


// TODO I think this is a good algorithm, but I want to test it out with more sample
// files. Then I need to write tests for both python and javascript versions. Then
// recalculate everything that is displayed to use our algorithms. And also put this
// into a beta so that the old method can run at the same time to start with.
// Set smoothing thresholds in meters.
export const calculateElevationGainFromTrackpoints = function (trackpoints, smooth_distance, smooth_height, return_plots_for_graphing) {

  let smoothed_elevation_points = getSmoothedElevationPointsByDistance(trackpoints, smooth_distance);

  if (smoothed_elevation_points.length == 0) {
    if (return_plots_for_graphing) {
      return {
        plots: [],
        elevation_gain: 0,
      }
    }
    else {
      return 0;
    }
  }

  // Calculate elevation gain based on the smoothed elevation points.
  let last_recorded_elevation = smoothed_elevation_points[0].elevation;
  let elevation_sections = [];
  let elevations_in_section = [];
  for (let i = 0; i < smoothed_elevation_points.length; i++) {

    // Add the first elevation.
    elevations_in_section.push(smoothed_elevation_points[i]);

    let elevation_delta = smoothed_elevation_points[i].elevation - last_recorded_elevation;

    // Wait until elevation has changed by our smooth height.
    if (Math.abs(elevation_delta) > smooth_height) {

      elevation_sections.push({
        climbing: (elevation_delta > 0) ? true : false,
        elevation_delta: elevation_delta,
        elevation_points: elevations_in_section,
      });

      last_recorded_elevation = smoothed_elevation_points[i].elevation;
      elevations_in_section = [];

    }

  }

  let total_elevation_gain = 0;
  let section_elevation_gain = 0;

  let all_section_elevation_gains = [];
  let all_section_plots = [];

  let section_plots = [];

  for (let i = 0; i < elevation_sections.length; i++) {

    if (elevation_sections[i].climbing) {

      total_elevation_gain += elevation_sections[i].elevation_delta;
      section_elevation_gain += elevation_sections[i].elevation_delta;

      section_plots.push({
        distance: elevation_sections[i].elevation_points[0].distance,
        elevation: elevation_sections[i].elevation_points[0].elevation
      });

      section_plots.push({
        distance: elevation_sections[i].elevation_points[elevation_sections[i].elevation_points.length - 1].distance,
        elevation: elevation_sections[i].elevation_points[elevation_sections[i].elevation_points.length - 1].elevation
      });

    }
    else if (section_plots.length > 0) {
      all_section_elevation_gains.push(section_elevation_gain);
      all_section_plots.push(section_plots);
      section_elevation_gain = 0;
      section_plots = [];
    }

  }

  if (!return_plots_for_graphing) {
    return total_elevation_gain;
  }

  if (section_plots.length > 0) {
    all_section_elevation_gains.push(section_elevation_gain);
    all_section_plots.push(section_plots);
  }
  
  return {
    section_plots: all_section_plots,
    section_elevation_gains: all_section_elevation_gains,
    total_elevation_gain: total_elevation_gain,
  }

}
